import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import apolloClient from '../../graphql/apollo-client';
import { GET_NOTICES, GET_NOTICE } from '../../graphql/queries';

const NoticeDetailPage: React.FC = () => {
  const { noticeId } = useParams<{ noticeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [notice, setNotice] = useState<any>(null);
  const [allNotices, setAllNotices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (!noticeId) return;
      setLoading(true);
      try {
        const [detailRes, listRes] = await Promise.all([
          apolloClient.query({ query: gql`${GET_NOTICE}`, variables: { noticeId }, fetchPolicy: 'network-only' }),
          apolloClient.query({ query: gql`${GET_NOTICES}`, fetchPolicy: 'cache-first' }),
        ]);
        setNotice(detailRes.data?.getNotice || null);
        const sorted = (listRes.data?.getNotices?.notices || [])
          .slice()
          .sort((a: any, b: any) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        setAllNotices(sorted);
      } catch (err) {
        console.error('Failed to fetch notice:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [noticeId]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-6 text-center text-gray-400">{t('loading')}</div>;
  if (!notice) return <div className="max-w-4xl mx-auto px-4 py-6 text-center text-gray-400">{t('notice.notFound')}</div>;

  const currentIdx = allNotices.findIndex((n: any) => n.noticeId === noticeId);
  const prevNotice = currentIdx > 0 ? allNotices[currentIdx - 1] : null;
  const nextNotice = currentIdx < allNotices.length - 1 ? allNotices[currentIdx + 1] : null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-xl font-bold text-gray-900 mb-2">{t('notice.title')}</h1>
      <hr className="border-gray-200 mb-6" />

      {/* Notice Header */}
      <div className="mb-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
            {notice.noticeType || '공지'}
          </span>
          <span className="text-xs text-gray-400">{notice.createdAt?.slice(0, 10)}</span>
        </div>
        <h2 className="text-lg font-bold text-gray-900">{notice.title}</h2>
      </div>

      {/* Notice Content */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 min-h-[200px]">
        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{notice.content}</p>
      </div>

      {/* Navigation */}
      <hr className="border-gray-200 mb-4" />
      <div className="flex items-center justify-between">
        <button
          onClick={() => prevNotice && navigate(`/notice/${prevNotice.noticeId}`)}
          disabled={!prevNotice}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t('notice.prev')}
        </button>
        <button
          onClick={() => navigate('/notice')}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {t('notice.list')}
        </button>
        <button
          onClick={() => nextNotice && navigate(`/notice/${nextNotice.noticeId}`)}
          disabled={!nextNotice}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t('notice.next')}
        </button>
      </div>
    </div>
  );
};

export default NoticeDetailPage;
